import { Component, Vue, PropSync, Watch } from 'vue-property-decorator';

import VirtualList from 'vue-virtual-scroll-list';

import TableItem from '@/components/table/tableitem/TableItem.vue';
import TableItemFront from '@/components/table/tableitemfront/TableItemFront.vue';
import TableHeaderItem from '@/components/table/tableheaderitem/TableHeaderItem.vue';
import TableRowMenu from '@/components/table/tablerowmenu/TableRowMenu.vue';
import TableCellMenu from '@/components/table/tablecellmenu/TableCellMenu.vue';
import TableEditValue from '@/components/table/tableeditvalue/TableEditValue.vue';
import UnsyncedList from '@/components/general/unsyncedlist/UnsyncedList.vue';
import UnitsMenu from '@/components/general/unitsmenu/UnitsMenu.vue';
import ProcessSelectMenu from '@/components/general/processselectmenu/ProcessSelectMenu.vue';
import ProcessHeaderItem from '@/components/table/processheaderitem/ProcessHeaderItem.vue';
import ProcessItem from '@/components/table/processitem/ProcessItem.vue';
import SubSectionSelect from '@/components/table/subsectionselect/SubSectionSelect.vue';
import HeaderBarTable from '@/components/general/headerbartable/HeaderBarTable.vue';
import ConfirmationCard from '@/components/general/confirmationMessage/ConfirmationCard.vue';

import { IHeaderBarComponentMethods } from '@/components/general/headerbartable/HeaderBarTable';

import { DataSetStore } from '@/store/store.dataSet';
import { ApiIdentifers } from '@/config/index';
import { ConfigurationSection } from '@/models/configuration/Section';
import { ConfigurationField } from '@/models/configuration/Field';
import { TableSubSectionFilter } from '@/models/tables/TableSubSectionFilter';
import { TableDataSet } from '@/models/tableDataset/TableDataSet';
import { Helpers } from '@/helpers/Helpers';
import { TableClickEvent } from '@/models/tables/TableClickEvent';
import { FieldTypeEnum } from '@/enums/FieldTypeEnums';
import { TableMenuClickEvent } from '@/models/tables/TableMenuClickEvent';
import { TableMenuActionEnums, TableMenuActionColorEnums } from '@/enums/TableMenuActionEnums';
import { TableLineCloneItem } from '@/models/tables/TableLineCloneItem';
import { DataSetSection } from '@/models/dataset/DataSetSection';
import { CsvExportService, DataSetService, EventsService, ExcelExportService, FilterNewService, SynchronizationService, ConfigurationService } from '@/services';
import { TableDataSetActionFieldCount } from '@/models/tableDataset/TableDataSetActionFieldCount';
import { LoggerService, UtilsHelper } from '@/utils';
import { TableDataSetField } from '@/models/tableDataset/TableDataSetField';
import { DexieDataSetEvent } from '@/models/dexie/UnsyncedDexieDataSet';
import { DataSet } from '@/models/dataset/DataSet';
import { DataSetEventTypeEnums } from '@/enums/DataSetEventTypeEnums';
import { EventStore } from '@/store/store.event';
import { TableDataSetColor } from '@/models/tables/TableDataSetColor';
import { TableItemColor } from '@/models/tables/TableItemColor';
import { DataSetField } from '@/models/dataset/DataSetField';

const subSectionRegexp = new RegExp(/__[0-9]+/g);

export interface IVirtualListMethods {
    setScrollTop(scrollPos: number): void;
    forceRender(): void;
    getSizes(): number;
}

@Component({
    components: {
        VirtualList,
        TableItem,
        TableItemFront,
        TableHeaderItem,
        TableRowMenu,
        TableCellMenu,
        TableEditValue,
        HeaderBarTable,
        UnsyncedList,
        UnitsMenu,
        ProcessSelectMenu,
        ProcessHeaderItem,
        ProcessItem,
        SubSectionSelect,
        ConfirmationCard
    }
})
export default class TableComponent extends Vue {
    @PropSync('filterName', { type: String }) public syncedFilterName!: string;
    @PropSync('myFilter', { type: String }) public myfilter!: string;

    public headerFrontItem: ConfigurationSection = new ConfigurationSection();

    public headerItems: ConfigurationField[] = [];

    public showProcess: boolean = false;
    public shrink: boolean = false;
    public buttonLineText: string = '';

    public selectSubSectionValue: string = '';
    public selectSubSectionData: TableSubSectionFilter[] = [];

    public selectSampleSubSectionValue: string = '';
    public selectSampleSubSectionData: TableSubSectionFilter[] = [];

    public identifers: any = ApiIdentifers;

    public showTableRowMenu: boolean = false;
    public showTableRowMenuDelay: number = 0;
    public selectedTableRowId: string = '';
    public selectedTableRow: TableClickEvent = new TableClickEvent();

    public showTableCellMenu: boolean = false;
    public showTableCellMenuDelay: number = 0;
    public selectedTableCellDataSetId: string = '';
    public selectedTableCellId: string = '';
    public selectedTableCell: TableClickEvent = new TableClickEvent();

    public dataSets: DataSet[] = [];
    public maxActionCounts: TableDataSetActionFieldCount[] = [];
    public actualSection: string = '';

    public processListScrolling: boolean = false;
    public processListTimeout: number | null = null;
    public processListOffset: number = 0;
    public fixedListScrolling: boolean = false;
    public fixedListTimeout: number | null = null;
    public listScrolling: boolean = false;
    public listTimeout: number | null = null;

    public listRefFixed: IVirtualListMethods | any = {};
    public listRef: IVirtualListMethods | any = {};
    public listRefProcess: IVirtualListMethods | any = {};
    public listScrollPos: number = 0;

    public showUnsynced: boolean = false;
    public showUnitsMenu: boolean = false;

    public showItemCount: boolean = false;
    public itemCountTitle: string = '';

    public lineCloneItems: TableLineCloneItem[] = [];
    public showProcessSelectMenu: boolean = false;

    public showTableEditValue: boolean = false;
    public showTableEditValueUnit: boolean = false;
    public tableEditValueField: TableDataSetField | undefined = new TableDataSetField();
    public tableEditValueUnitTypeField: TableDataSetField | undefined = new TableDataSetField();

    public tableDataSetColors: TableDataSetColor[] = [];

    public quickCopyToast: boolean = false;
    public quickCopyToastTimeout: number = 2000;
    public quickCopyToastText: string = '';

    public fixedRowIdentifiers: string[] = [];
    public fixedRowItems: DataSet[] = [];

    public processHeaderItems: ConfigurationField[] = [];

    public showChangedValues: boolean = false;
    public isMounting: boolean = false;
    public isLoading: boolean = false;
    public showModal: boolean = false;

    public filterResults: string = '';

    public emptyTableDataSet: TableDataSet = DataSetService.GenerateTableDataSetEmpty();

    public virtualListItemsRemain = 13;

    public filterTitle = '';

    public async beforeMount() {
        LoggerService.LogDebug(`beforeMount()`);
        this.isMounting = true;
        this.isLoading = true;
    }

    // tslint:disable-next-line: no-empty
    public mounted() {
        LoggerService.LogDebug(`TableComponent mounted`);
        window.addEventListener('resize', this.onResize);

        this.isLoading = true;
        this.isMounting = true;

        this.$nextTick(async () => {
            await this.initData();
            if (this.myfilter && this.myfilter === 'true') {
                const filter = FilterNewService.UsedFilterForDataSets;
                if (filter) {
                    this.filterTitle = filter.isUnsaved || filter.filterName === '' ? 'unsaved*' : filter.filterName;
                }
            } else {
                FilterNewService.ResetUsedFilterData();
                this.filterTitle = '';
            }
            this.isMounting = false;
        });
    }

    public updated() {
        LoggerService.LogDebug(`TableComponent updated`);

        if (!this.listRef) {
            this.listRefFixed = Helpers.getReference<IVirtualListMethods>(this.$refs, 'fixed-list-items');
            this.listRef = Helpers.getReference<IVirtualListMethods>(this.$refs, 'list-items');
            this.listRefProcess = Helpers.getReference<IVirtualListMethods>(this.$refs, 'process-list-items');
        }
        this.setUnsyncedEventsmarker();
        this.setDataSetColors();
    }

    public beforeDestroy() {
        LoggerService.LogDebug(`TableComponent beforeDestroy`);
        window.removeEventListener('resize', this.onResize);
        DataSetService.OnUpdate.off(() => null);
        if (DataSetStore.GetUseOldDataSet === false) {
            DataSetService.ClearAllTableDataSets();
            DataSetService.ClearAllFilteredDataSets();
            this.headerItems = [];
            this.processHeaderItems = [];
            this.fixedRowIdentifiers = [];
            this.clearState();
        }
    }

    public onResize() {
        this.$nextTick(async () => {
            this.setTableHeight();
        });
    }

    @Watch('showTableRowMenu')
    public onShowTableRowMenuChanged(newValue: boolean) {
        // LoggerService.LogDebug(`onShowTableRowMenuChanged: ${newValue}`);
        if (newValue === false) {
            this.selectedTableRowId = '';
        }
    }

    @Watch('showTableCellMenu')
    public onShowTableCellMenuChanged(newValue: boolean) {
        // LoggerService.LogDebug(`onShowTableCellMenuChanged: ${newValue}`);
        if (newValue === false) {
            this.selectedTableCellDataSetId = '';
            this.selectedTableCellId = '';
        }
    }

    @Watch('actualSection')
    public onActualSectionChanged(newValue: string) {
        // LoggerService.LogDebug(`onActualSectionChanged: ${newValue}`);
        if (newValue === ApiIdentifers.material || newValue === ApiIdentifers.contacts || newValue === ApiIdentifers.sample) {
            this.showItemCount = true;
            this.itemCountTitle = `${this.$vuetify.lang.t('$vuetify.identifiers.' + newValue)}`;
            if (this.itemCountTitle.substr(this.itemCountTitle.length - 1) !== 's') {
                this.itemCountTitle += 's';
            }
        } else {
            this.showItemCount = false;
            this.itemCountTitle = '';
        }
    }

    @Watch('fixedRowIdentifiers', { deep: true, immediate: false })
    public onFixedRowIdentifiersChanged(newValue: string[]) {
        // LoggerService.LogDebug(`onFixedRowIdentifiersChanged: `, newValue);
        this.setFixedRows();
    }

    @Watch('processHeaderItems', { deep: true, immediate: false })
    public onProcessHeaderItemsChanged(newValue: ConfigurationField[]) {
        // LoggerService.LogDebug(`onFixedRowIdentifiersChanged: `, newValue);
        if (this.isMounting === false) {
            this.isLoading = true;
            this.setProcessFilter();
            this.isLoading = false;
        }
    }

    get getConfiguration() {
        return ConfigurationService.configuration;
    }

    get getMachinesConfiguration() {
        return ConfigurationService.machinesConfiguration;
    }

    get getSamplesConfiguration() {
        return ConfigurationService.samplesConfiguration;
    }

    get getMaxActionCounts() {
        return DataSetStore.maxActionCounts;
    }

    public async initData(fromFilter?: string) {
        const useFilter = this.myfilter && this.myfilter === 'true' ? true : false;

        await this.getDataSets(useFilter);

        DataSetService.OnUpdate.on(inDataSetIds => {
            this.updateDataSets(inDataSetIds, useFilter);
        });

        this.emptyTableDataSet = DataSetService.GenerateTableDataSetEmpty();

        this.maxActionCounts = this.getMaxActionCounts;
        this.setUnsyncedEventsmarker();

        this.buttonLineText = this.$vuetify.lang.t('$vuetify.table.controlClosed');

        /**
         * Header items for process view
         */
        const machineOrder = this.getConfiguration.sections.find(sec => sec.identifier === ApiIdentifers.machineOrder);
        if (machineOrder !== undefined && machineOrder.fields) {
            this.processHeaderItems.splice(0, this.processHeaderItems.length);
            machineOrder.fields.forEach(mo => {
                if (mo.identifier !== ApiIdentifers.machineOrderAll) {
                    this.processHeaderItems.push(new ConfigurationField(mo));
                }
            });
        }
        /**
         * Get general section extra for static header fields
         */
        this.headerFrontItem = this.getConfiguration.sections.find(sec => sec.identifier === ApiIdentifers.general) || new ConfigurationSection();

        /**
         * By default we use general section for normal header fields
         */
        this.getHeaderFilterItems();
        // this.headerSection = this.getConfiguration.sections.find((sec) => sec.identifier === ApiIdentifers.general) || new ConfigurationSection();
        // this.getHeaderSubSections(this.headerSection);

        this.listRefFixed = Helpers.getReference<IVirtualListMethods>(this.$refs, 'fixed-list-items');
        this.listRef = Helpers.getReference<IVirtualListMethods>(this.$refs, 'list-items');
        this.listRefProcess = Helpers.getReference<IVirtualListMethods>(this.$refs, 'process-list-items');

        this.$nextTick(async () => {
            if (DataSetStore.GetUseOldDataSet === true) {
                await this.setState();
            }
            this.setTableHeight();
            this.isLoading = false;
        });
    }

    public onBack() {
        FilterNewService.UsedFilterForDataSetFilters = [];
        FilterNewService.UsedFilterForDataSets = undefined;
        this.$router.replace({ name: 'dashboard' });
    }

    public onEditFilter() {
        if (this.myfilter !== undefined) {
            this.$router.push({ name: 'filterNew', query: { myFilter: 'edit' } });
        } else {
            this.$router.push('filterNew');
        }
    }

    public onCard(cardIdentifier: string) {
        this.actualSection = cardIdentifier;
        if (this.isMounting === false) {
            this.getHeaderFilterItems();
        }
    }

    public addNew() {
        this.$router.push({ name: 'edit' });
    }

    public onToggleLine() {
        this.showProcess = !this.showProcess;
        if (!this.showProcess) {
            this.buttonLineText = this.$vuetify.lang.t('$vuetify.table.controlClosed');
        } else {
            window.setTimeout(() => {
                if (this.listRefProcess) {
                    this.listRefProcess.setScrollTop(this.processListOffset);
                }
            }, 250);

            this.buttonLineText = this.$vuetify.lang.t('$vuetify.table.controlOpen');
        }
        LoggerService.LogDebug(`onToggleLine pressed: ${this.showProcess}`);
    }

    public async onMenuItemClicked(event: TableMenuClickEvent) {
        const dataSet = this.dataSets.find(ds => ds.dataSetId === event.dataSetId);
        if (dataSet !== undefined) {
            switch (event.action) {
                case TableMenuActionEnums.EDIT_LINE:
                    if (dataSet.id !== undefined) {
                        this.saveState();
                        this.$router.push({ name: 'edit', params: { id: dataSet.id.toString() } });
                    }
                    break;
                case TableMenuActionEnums.EDIT_VALUE:
                    if (dataSet.id !== undefined && event.dataSetCellId !== undefined) {
                        this.editCellValue(dataSet, event.dataSetCellId || '');
                    }
                    break;
                case TableMenuActionEnums.COLOUR_FIELD:
                    if (dataSet.id !== undefined && event.dataSetCellId !== undefined) {
                        const field = this.getEditField(dataSet, event.dataSetCellId, this.actualSection, this.selectSubSectionValue, this.selectSampleSubSectionValue);
                        if (field !== undefined) {
                            field.color = event.color || TableMenuActionColorEnums.UNMARK;
                            this.addDataSetColor(dataSet.dataSetId || '', field.identifier, field.color, this.selectSubSectionValue, this.selectSampleSubSectionValue);
                        } else {
                            LoggerService.LogWarn(`could not find field(${event.dataSetCellId}) in dataset: `, dataSet);
                        }
                    }
                    break;
                case TableMenuActionEnums.CLONE_LINE:
                    if (dataSet.id !== undefined) {
                        this.cloneLine(dataSet);
                    }
                    break;

                case TableMenuActionEnums.EXPORT_TEST_SERIES_CSV:
                case TableMenuActionEnums.EXPORT_TEST_SERIES_EXCEL:
                    const testSeries = dataSet.sections[0].fields.find(field => field.identifier === ApiIdentifers.generalTestSeries);
                    if (testSeries !== undefined) {
                        this.isLoading = true;
                        const dataSetsToExport = this.dataSets.filter(ds => {
                            const otherTestSeries = ds.sections[0].fields.find(field => field.identifier === ApiIdentifers.generalTestSeries);
                            if (otherTestSeries !== undefined) {
                                return otherTestSeries.valueString === testSeries.valueString;
                            }
                            return false;
                        });
                        if (event.action === TableMenuActionEnums.EXPORT_TEST_SERIES_CSV) {
                            // just use a timeout as workaround tho show the spinner, before the export blocks the ui thread
                            window.setTimeout(async () => {
                                CsvExportService.generateCsvFromDataSets(dataSetsToExport).then(() => {
                                    this.isLoading = false;
                                });
                            }, 500);
                        } else {
                            // just use a timeout as workaround tho show the spinner, before the export blocks the ui thread
                            window.setTimeout(async () => {
                                ExcelExportService.generateExcelFromDataSets(dataSetsToExport).then(() => {
                                    this.isLoading = false;
                                });
                            }, 500);
                        }
                    }
                    break;
                case TableMenuActionEnums.CLEAN_ALL_MARKS:
                    this.resetDataSetMarkers();
                    break;
                case TableMenuActionEnums.COPY_FOR_QUICK_PASTE:
                    this.quickCopyValue(this.selectedTableCell.dataSetCellValue, event.dataSetCellId);
                    break;
                case TableMenuActionEnums.FIX_ROW:
                    if (dataSet.dataSetId !== undefined) {
                        if (this.fixedRowIdentifiers.length < 5) {
                            this.fixedRowIdentifiers.push(dataSet.dataSetId);
                        } else {
                            LoggerService.LogWarn(`Maximal number of fixed rows (5) exeded. Ignoring this one.`);
                        }
                    }
                    break;
                case TableMenuActionEnums.UNFIX_ROW:
                    if (dataSet.dataSetId !== undefined) {
                        const index = this.fixedRowIdentifiers.findIndex(item => item === dataSet.dataSetId);
                        if (index >= 0) {
                            this.fixedRowIdentifiers.splice(index, 1);
                        }
                    }
                    break;
                case TableMenuActionEnums.SETTINGS_REPORT:
                    this.saveState();
                    this.$router.push({ name: 'settingsReport', params: { dataSetId: dataSet.dataSetId || '' } });
                    break;
                case TableMenuActionEnums.COPY_TAB_ID:
                    if (dataSet.dataSetId !== undefined) {
                        this.copyStringToClipboard(dataSet.dataSetId);
                    }
                    break;
                case TableMenuActionEnums.COPY_TAB_ID_URL:
                    if (dataSet.dataSetId !== undefined) {
                        const origin = window.location.origin;
                        this.copyStringToClipboard(`${origin}/#/open/${dataSet.dataSetId}`);
                    }
                    break;
                case TableMenuActionEnums.PREVIEW_DATASET:
                    if (dataSet.id !== undefined) {
                        this.saveState();
                        this.$router.push({ name: 'preview', params: { id: dataSet.id.toString() } });
                    }
                    break;
                default:
                    LoggerService.LogWarn(`Row menu action "${TableMenuActionEnums[event.action]} not supported yet`);
                    break;
            }
        }
    }

    public async onExportAllData() {
        this.isLoading = true;
        // just use a timeout as workaround tho show the spinner, before the export blocks the ui thread
        window.setTimeout(async () => {
            await ExcelExportService.generateExcelFromDataSets().then(() => {
                this.isLoading = false;
            });
        }, 500);
    }

    public onTableEditValueClose(save: boolean) {
        if (save === true) {
            this.editDataSetField();
        }
    }

    public onSectionChanged(inEvent: string) {
        this.getHeaderFilterItems(inEvent);
    }

    public onSampleSectionChanged(inEvent: string) {
        LoggerService.LogDebug(`onSampleSectionChanged`, inEvent);
        this.selectSampleSubSectionValue = inEvent;
    }

    public GetHeaderFrontField(inIdentifier: string): string {
        // return (this.headerFrontItem.fields.find((field) => field.identifier === inIdentifier) || new ConfigurationField()).name || '-';
        let ret: string = '-';
        if (this.headerFrontItem.fields.some(field => field.identifier === inIdentifier)) {
            const text = this.getTextFromLanguageFile(inIdentifier);
            if (text !== undefined) {
                ret = text;
            }
        } else if (inIdentifier === 'tabId') {
            // for tabID
            ret = this.$vuetify.lang.t('$vuetify.table.header.tabId');
        }
        return ret;
    }

    public getfilterUnitFormatFields(fields: ConfigurationField[]) {
        return fields.filter(f => f.type !== FieldTypeEnum.UNIT);
    }

    /**
     * Method to fetch translation with fallback for ConfigurationField
     * @param field ConfigurationField
     */
    public getIdentifierText(inField: ConfigurationField): string {
        let ret: string = '';
        if (inField.identifier) {
            const text = this.getTextFromLanguageFile(inField.identifier);
            if (text === undefined || text.includes('$vuetify.identifiers.')) {
                ret = inField.name;
            } else {
                ret = text;
            }
        }
        return ret;
    }

    public getTextFromLanguageFile(inIdentifier: string): string | undefined {
        let ret: string | undefined;
        if (inIdentifier) {
            ret = this.$vuetify.lang.t(`$vuetify.identifiers.${inIdentifier}`);
            if (ret.length === 0 && ret.includes('$vuetify.identifiers.')) {
                /**
                 * In case the text was not found
                 */
                ret = undefined;
            }
        }

        return ret;
    }

    public onScrollProcess(event: any, data: any) {
        // close row menu to avoid wrong pos
        this.showTableRowMenu = false;
        this.showTableCellMenu = false;
        if (this.fixedListScrolling === false && this.listScrolling === false) {
            this.processListScrolling = true;
            if (this.processListTimeout !== null) {
                window.clearTimeout(this.processListTimeout);
            }
            this.processListTimeout = window.setTimeout(() => {
                this.processListScrolling = false;
            }, 100);

            if (this.listRef && this.listRefFixed) {
                this.listRef.setScrollTop(data.offset);
                this.listRefFixed.setScrollTop(data.offset);
            }
            this.processListOffset = data.offset;
            this.listScrollPos = data.offset;
        }
    }

    public onScrollFixed(event: any, data: any) {
        // close row menu to avoid wrong pos
        this.showTableRowMenu = false;
        this.showTableCellMenu = false;
        if (this.processListScrolling === false && this.listScrolling === false) {
            this.fixedListScrolling = true;
            if (this.fixedListTimeout !== null) {
                window.clearTimeout(this.fixedListTimeout);
            }
            this.fixedListTimeout = window.setTimeout(() => {
                this.fixedListScrolling = false;
            }, 100);

            if (this.listRef && this.listRefProcess) {
                this.listRef.setScrollTop(data.offset);
                this.listRefProcess.setScrollTop(data.offset);
            }
            this.processListOffset = data.offset;
            this.listScrollPos = data.offset;
        }
    }

    public onScrollItems(event: any, data: any) {
        // close row menu to avoid wrong pos
        this.showTableRowMenu = false;
        this.showTableCellMenu = false;
        if (this.processListScrolling === false && this.fixedListScrolling === false) {
            this.listScrolling = true;
            if (this.listTimeout !== null) {
                window.clearTimeout(this.listTimeout);
            }
            this.listTimeout = window.setTimeout(() => {
                this.listScrolling = false;
            }, 100);

            const scrollerRefX = Helpers.getReference<HTMLDivElement>(this.$refs, 'table-header');
            scrollerRefX.scrollLeft = event.currentTarget.scrollLeft;

            if (this.listRefFixed && this.listRefProcess) {
                this.listRefFixed.setScrollTop(data.offset);
                this.listRefProcess.setScrollTop(data.offset);
            }
            this.processListOffset = data.offset;
            this.listScrollPos = data.offset;
        }
    }

    public onFrontItemClicked(event: TableClickEvent) {
        // if the row menu is already open, close it first and open delayed
        if (this.showTableCellMenu === true || this.showTableRowMenu === true) {
            this.showTableCellMenu = false;
            this.showTableRowMenu = false;
            this.showTableRowMenuDelay = 100;
        } else {
            this.showTableRowMenuDelay = 0;
        }

        window.setTimeout(() => {
            event.isFixed = this.fixedRowIdentifiers.some(id => id === event.dataSetId);
            this.selectedTableRowId = event.dataSetId;
            this.selectedTableRow = event;
            this.showTableRowMenu = true;
        }, this.showTableRowMenuDelay);
    }

    public onTableItemClicked(event: TableClickEvent) {
        // if the row menu is already open, close it first and open delayed
        if (this.showTableCellMenu === true || this.showTableRowMenu === true) {
            this.showTableCellMenu = false;
            this.showTableRowMenu = false;
            this.showTableCellMenuDelay = 100;
        } else {
            this.showTableCellMenuDelay = 0;
        }

        const dataSet = this.dataSets.find(ds => ds.dataSetId === event.dataSetId);
        if (dataSet !== undefined) {
            const editField = this.getEditField(dataSet, event.dataSetCellId || '', this.actualSection, this.selectSubSectionValue, this.selectSampleSubSectionValue);
            event.disableColors = editField === undefined;

            if (this.actualSection === ApiIdentifers.material) {
                event.disableEdit = !this.checkActionSection(dataSet, ApiIdentifers.material, this.selectSubSectionValue);
            } else if (this.actualSection === ApiIdentifers.contacts) {
                event.disableEdit = !this.checkActionSection(dataSet, ApiIdentifers.contacts, this.selectSubSectionValue);
            } else if (this.actualSection === ApiIdentifers.sample) {
                event.disableEdit = !this.checkActionSection(dataSet, ApiIdentifers.sample, this.selectSubSectionValue, this.selectSampleSubSectionValue);
            }
        }

        window.setTimeout(() => {
            event.isFixed = this.fixedRowIdentifiers.some(id => id === event.dataSetId);
            this.selectedTableCellDataSetId = event.dataSetId;
            this.selectedTableCellId = event.dataSetCellId || '';
            this.selectedTableCell = event;
            this.showTableCellMenu = true;
        }, this.showTableCellMenuDelay);
    }

    public onItemDbClicked(event: string) {
        LoggerService.debug(`onItemDbClicked`, event);
        this.onMenuItemClicked(
            new TableMenuClickEvent({
                action: TableMenuActionEnums.PREVIEW_DATASET,
                dataSetId: event
            })
        );
    }

    public onUnsyncedList() {
        this.showUnsynced = true;
    }

    public showUntisMenu() {
        this.showUnitsMenu = true;
    }

    public async onCloneClicked() {
        /**
         * get identififer depending on what was clicked
         */
        const identifier = this.selectedTableRow && this.selectedTableRow.dataSetId !== '' ? this.selectedTableRow.dataSetId : this.selectedTableCell.dataSetId;
        const dataSet = DataSetService.GetAllDataSets().find(ds => ds.dataSetId === identifier);
        await DataSetStore.ClearCloneLineData();
        if (dataSet !== undefined) {
            const cloneData = dataSet.sections.filter(this.filterCloneSections(this.lineCloneItems));
            await DataSetStore.SetCloneLineData(cloneData);
            this.saveState();
            this.$router.push({ name: 'clone', params: { cloneId: dataSet.dataSetId || '' } });
        }
    }

    public showChanged(changed: boolean) {
        this.showChangedValues = changed;
    }

    public selectedFilterStrings() {
        return FilterNewService.UsedFilterForDataSetFilters;
    }

    private filterCloneSections(filterSections: TableLineCloneItem[]): any {
        return (inSec: DataSetSection) => {
            const isSelected = filterSections.find(fs => fs.sectionIdentifier === inSec.identifier);
            return isSelected !== undefined ? isSelected.selected : false;
        };
    }

    private cloneLine(inDataSet: DataSet) {
        this.lineCloneItems.splice(0, this.lineCloneItems.length);
        if (inDataSet.id != null) {
            const dataSet = DataSetService.GetAllDataSets().find(ds => ds.id === inDataSet.id);
            if (dataSet !== undefined) {
                dataSet.sections.forEach(sec => {
                    this.lineCloneItems.push(new TableLineCloneItem({ sectionIdentifier: sec.identifier, selected: false }));
                });

                this.showProcessSelectMenu = true;
            }
        }
    }

    private getHeaderFilterItems(subSectionIdentifier?: string) {
        // sectionIdentifier = sectionIdentifier.replace(subSectionRegexp, '');
        const selectedSectionIdentifier = this.actualSection;
        if (selectedSectionIdentifier === undefined || selectedSectionIdentifier.length === 0) {
            LoggerService.LogDebug(`getHeaderFilterItems sectionIdentifier is empty`);
            return;
        }

        let inConfig = this.getConfiguration.sections.find(sec => sec.identifier === selectedSectionIdentifier);
        if (inConfig === undefined) {
            inConfig = this.getMachinesConfiguration.sections.find(sec => sec.identifier === selectedSectionIdentifier);
        }
        if (inConfig === undefined) {
            inConfig = this.getSamplesConfiguration.sections.find(sec => sec.identifier === selectedSectionIdentifier);
        }
        if (inConfig !== undefined) {
            // create new object to avoid change vuex object without mutation
            inConfig = new ConfigurationSection(inConfig);
            let sectionIdentifier = inConfig.identifier;
            let sectionName = inConfig.name; // TODO: Replace with identifier string fromn language.json
            let fields = inConfig.fields;
            if (subSectionIdentifier !== undefined) {
                for (const field of inConfig.fields) {
                    const subSection = this.getSubSection(field, subSectionIdentifier);
                    if (subSection !== undefined) {
                        sectionIdentifier = subSection.identifier;
                        sectionName = subSection.name;
                        fields = subSection.fields || [];
                        break;
                    }
                }
            }

            this.sortSectionsFields(fields);

            this.headerItems = this.getHeaderSubSections(fields, subSectionIdentifier);

            if (subSectionIdentifier === undefined) {
                this.headerItems.splice(0, this.headerItems.length);

                /*
                 * If we have a machine section, we need to test if there are top single fields
                 * e.g. for "Total Waste Blow Room"  we need to add a fake subSection...
                 */
                if (this.checkIfIsMachineSection(selectedSectionIdentifier)) {
                    const topSingleFields = fields.filter(field => field.fields === undefined);
                    const tmpField = new ConfigurationField();
                    tmpField.name = `${inConfig.name}__singleFields`;
                    tmpField.identifier = `${inConfig.identifier}`;
                    tmpField.fields = topSingleFields;
                    fields.unshift(tmpField);
                }

                if (inConfig.identifier === ApiIdentifers.sample) {
                    this.headerItems = this.getHeaderSubSections(fields[0].fields || [], ApiIdentifers.sampleLaboratory);
                } else {
                    this.headerItems = this.getHeaderSubSections(fields, subSectionIdentifier);
                }

                let items: TableSubSectionFilter[] = [];
                const sampleItems: TableSubSectionFilter[] = [];
                this.headerItems.forEach(item => {
                    if (item.fields !== undefined) {
                        items.push(new TableSubSectionFilter({ value: item.identifier, text: this.getIdentifierText(item) }));
                    }
                });
                // in case of action we need to add manual
                // also samples need special handling
                // TODO: When max add action is implemented in backend, handle it here too
                if (inConfig.action !== undefined && inConfig.identifier === ApiIdentifers.sample) {
                    // handle sample sections
                    items = [];
                    this.getHeaderSubSections(fields, ApiIdentifers.sampleLaboratory).forEach(item => {
                        items.push(new TableSubSectionFilter({ value: item.identifier, text: this.getIdentifierText(item) }));
                    });
                    sampleItems.push(new TableSubSectionFilter({ value: `${inConfig.identifier}__0`, text: `${inConfig.name} ${1}` }));
                    const actionCount = this.maxActionCounts.find(mac => mac.identifier === (inConfig && inConfig.identifier));
                    if (actionCount !== undefined) {
                        for (let index = 1; index < actionCount.fieldCount; index++) {
                            sampleItems.push(new TableSubSectionFilter({ value: `${inConfig.identifier}__${index}`, text: `${inConfig.name} ${index + 1}` }));
                        }
                    }
                    this.selectSampleSubSectionValue = subSectionIdentifier || `${inConfig.identifier}__0`;
                } else if (inConfig.action !== undefined && items.length === 0) {
                    // handle other action sections
                    items.push(new TableSubSectionFilter({ value: `${sectionIdentifier}__0`, text: `${sectionName} ${1}` }));
                    const actionCount = this.maxActionCounts.find(mac => mac.identifier === sectionIdentifier);
                    if (actionCount !== undefined) {
                        for (let index = 1; index < actionCount.fieldCount; index++) {
                            items.push(new TableSubSectionFilter({ value: `${sectionIdentifier}__${index}`, text: `${sectionName} ${index + 1}` }));
                        }
                    }
                    this.selectSubSectionValue = subSectionIdentifier || `${sectionIdentifier}__0`;
                } else {
                    if (!this.selectSubSectionValue === false) {
                        const subSec = this.headerItems.find(field => field.identifier === this.selectSubSectionValue);
                        if (subSec !== undefined && subSec.fields !== undefined) {
                            this.headerItems = this.getHeaderSubSections(subSec.fields, this.selectSubSectionValue);
                        }
                    } else if (this.actualSection === ApiIdentifers.general) {
                        // add notes field manual to general section
                        const notes = this.getConfiguration.sections.find(sec => sec.identifier === ApiIdentifers.notes);
                        if (notes != null) {
                            const notesInputText = notes.fields.find(field => field.identifier === ApiIdentifers.notesInputText);
                            if (notesInputText != null) {
                                this.headerItems.push(notesInputText);
                            }
                        }
                        // add manual items for creator/editor
                        this.headerItems.push(UtilsHelper.generateUserConfigurationField(ApiIdentifers.generalCreatedBy, 'Created by'));
                        this.headerItems.push(UtilsHelper.generateUserConfigurationField(ApiIdentifers.generalCreatedAt, 'Created at'));
                        this.headerItems.push(UtilsHelper.generateUserConfigurationField(ApiIdentifers.generalEditedBy, 'Modified by'));
                        this.headerItems.push(UtilsHelper.generateUserConfigurationField(ApiIdentifers.generalEditedAt, 'Modified at'));
                        this.headerItems.push(UtilsHelper.generateUserConfigurationField(ApiIdentifers.generalMyMillTimestamp, 'MyMill Date'));
                    }
                }
                this.selectSubSectionData.splice(0, this.selectSubSectionData.length);
                this.selectSampleSubSectionData.splice(0, this.selectSampleSubSectionData.length);
                this.selectSubSectionData = items;
                this.selectSampleSubSectionData = sampleItems;
                if (this.selectSampleSubSectionData.length === 0) {
                    this.selectSampleSubSectionValue = '';
                }
            } else {
                const filteredFields = fields.filter(field => field.fields === undefined);
                if (filteredFields.length === 0) {
                    this.headerItems = this.getHeaderSubSections(fields, subSectionIdentifier);
                } else {
                    this.headerItems = this.getHeaderSubSections(filteredFields, subSectionIdentifier);
                }
            }

            fields.splice(0, fields.length);
        } else {
            LoggerService.LogError(`Could not find section: ${selectedSectionIdentifier}`);
        }
    }

    private getHeaderSubSections(inFields: ConfigurationField[], subSectionIdentifier?: string): ConfigurationField[] {
        let identifires: ConfigurationField[] = [];

        inFields.forEach(field => {
            if (field.fields) {
                // identifires.push(field.identifier);
                identifires = identifires.concat(this.getHeaderSubSectionsFromField(field));
            }
        });

        if (identifires.length === 0) {
            identifires = inFields.filter(field => field.fields === undefined);
            this.selectSubSectionValue = subSectionIdentifier || '';
        } else {
            this.selectSubSectionValue = subSectionIdentifier || identifires[0].identifier || '';
        }

        return identifires;
    }

    private getSubSection(inField: ConfigurationField, subSectionIdentifier?: string): ConfigurationField | undefined {
        let subField;
        if (subSectionIdentifier !== undefined && inField.fields !== undefined) {
            if (inField.identifier === subSectionIdentifier) {
                subField = inField;
            } else {
                subField = inField.fields.find(field => field.identifier === subSectionIdentifier);
                if (subField === undefined) {
                    for (const field of inField.fields) {
                        subField = this.getSubSection(field, subSectionIdentifier);
                        if (subField !== undefined) {
                            break;
                        }
                    }
                }
            }
        }
        return subField;
    }

    private getHeaderSubSectionsFromField(inField: ConfigurationField): ConfigurationField[] {
        let identifiers: ConfigurationField[] = [];

        if (inField.fields) {
            inField.fields.forEach(field => {
                const subIdentifires = this.getHeaderSubSectionsFromField(field);
                if (subIdentifires.length > 0) {
                    identifiers = identifiers.concat(subIdentifires);
                }
            });
            if (inField.fields.length !== identifiers.length) {
                identifiers.unshift(inField);
            }
        }

        return identifiers;
    }

    private async editDataSetField() {
        const editDataSet = this.dataSets.find(ds => ds.dataSetId === this.selectedTableCell.dataSetId);
        if (editDataSet !== undefined && this.selectedTableCell.dataSetCellId) {
            const editDataSetCopy = new DataSet(editDataSet);
            const editField = this.getEditField(editDataSetCopy, this.selectedTableCell.dataSetCellId, this.actualSection, this.selectSubSectionValue, this.selectSampleSubSectionValue);
            if (editField !== undefined && this.tableEditValueField) {
                editField.valueBool = this.tableEditValueField.valueBool;
                editField.valueNumber = this.tableEditValueField.valueNumber;
                editField.valueString = this.tableEditValueField.valueString;

                if (this.tableEditValueField.type === FieldTypeEnum.NUMBER && this.tableEditValueField.unit !== undefined && this.tableEditValueUnitTypeField !== undefined) {
                    const unitTypeEditField = this.getEditField(
                        editDataSetCopy,
                        this.tableEditValueUnitTypeField.identifier || '',
                        this.actualSection,
                        this.selectSubSectionValue,
                        this.selectSampleSubSectionValue
                    );
                    if (unitTypeEditField !== undefined) {
                        unitTypeEditField.valueNumber = this.tableEditValueUnitTypeField.valueNumber;
                    }
                }

                await EventsService.PutUnsyncedEvent(
                    new DexieDataSetEvent({
                        dataSet: editDataSetCopy as DataSet,
                        event: DataSetEventTypeEnums.UPDATE,
                        date: new Date().toISOString()
                    })
                )
                    .then(async (data: number) => {
                        LoggerService.LogDebug(`Saved section as local unsynced dataSet number: ${data}`);
                        await EventStore.SetUnsyncedEvents();
                        editDataSet.unsyncedParts = true;
                        if (SynchronizationService.IsRunnung() === true) {
                            /**
                             * only sync now if auto sync is running
                             * this only needed for debug mode so we can
                             * see unsynced events better even if online
                             */
                            SynchronizationService.SyncNow();
                        }
                    })
                    .catch((error: any) => {
                        LoggerService.LogError(`Error on saving: `, error);
                    });
            }
        }
    }

    private getEditField(inDataSet: DataSet, inFieldId: string, inSectionId: string, inSubSectionId: string = '', inSampleSubSectionId: string = ''): DataSetField | undefined {
        const section = inDataSet.sections.find(sec => sec.identifier === inSectionId);
        if (section !== undefined && section.fields !== undefined) {
            let editField: DataSetField | undefined;
            for (const field of section.fields) {
                editField = this.getField(field, inFieldId, inSubSectionId, inSampleSubSectionId);
                if (editField !== undefined) {
                    return editField;
                    break;
                }
            }
        }

        return undefined;
    }

    private getField(inField: DataSetField, inFieldId: string, inSubSectionId: string = '', inSampleSubSectionId: string = ''): DataSetField | undefined {
        let field: DataSetField | undefined;
        if (inField.identifier === inFieldId) {
            field = inField;
        } else if (inSampleSubSectionId !== '') {
            if (inField.identifier === inSampleSubSectionId) {
                field = this.getField(inField, inFieldId, inSubSectionId);
            }
        } else if (inSubSectionId !== '') {
            if (inField.identifier === inSubSectionId) {
                field = this.getField(inField, inFieldId);
            } else if (inField.fields !== undefined) {
                const subSection = inField.fields.find(subSec => subSec.identifier === inSubSectionId);
                if (subSection !== undefined && subSection.fields !== undefined) {
                    field = this.getField(subSection, inFieldId);
                }
            }
        } else if (inField.fields !== undefined) {
            for (const subField of inField.fields) {
                const subFiledRes = this.getField(subField, inFieldId);
                if (subFiledRes !== undefined) {
                    field = subFiledRes;
                    break;
                }
            }
        }
        return field;
    }

    private getTableField(inFieldId: string, inSectionId: string): TableDataSetField | undefined {
        let field: TableDataSetField | undefined;
        const section = this.emptyTableDataSet.sections.find(sec => sec.identifier === inSectionId);
        if (section !== undefined) {
            field = this.getTableFieldFromSection(inFieldId, section.fields);
        }
        return field;
    }

    private getTableFieldFromSection(inFieldId: string, inFields: TableDataSetField[]): TableDataSetField | undefined {
        let field: TableDataSetField | undefined;
        for (const inField of inFields) {
            if (inField.identifier === inFieldId) {
                field = inField;
                break;
            } else if (inField.fields !== undefined) {
                field = this.getTableFieldFromSection(inFieldId, inField.fields);
                if (field !== undefined) {
                    break;
                }
            }
        }
        return field;
    }

    private setUnsyncedEventsmarker() {
        const unsyncedEvents = EventStore.GetUnsyncedEvents;
        // reset markers first
        this.dataSets.forEach(ds => {
            ds.unsyncedParts = false;
        });
        // set markers for datasets with unsynced events
        this.dataSets.filter(this.filterUnsynced(unsyncedEvents)).forEach(ds => {
            ds.unsyncedParts = true;
        });
    }

    private filterUnsynced(inUnsyncedEvents: DexieDataSetEvent[]): any {
        return (inTableDataSet: TableDataSet) => {
            const match = inUnsyncedEvents.find(ue => ue.dataSet.dataSetId === inTableDataSet.dataSetId);
            return match !== undefined;
        };
    }

    /**
     * Method to add a colored field to the tableview "session object"
     * @param inDataSetId           Identifier of the dataset
     * @param inFieldId             Identifier of the field
     * @param inColor               Color of the field
     * @param inSubSectionId        Optional sub-section id, e.g. for materials
     * @param inSampleSubSectionId  Optional sample-sub-section id
     */
    private addDataSetColor(inDataSetId: string, inFieldId: string, inColor: TableMenuActionColorEnums, inSubSectionId?: string, inSampleSubSectionId?: string) {
        const dataSetColor = this.tableDataSetColors.find(dsc => dsc.dataSetid === inDataSetId);
        if (dataSetColor !== undefined) {
            let dataFieldColor: TableItemColor | undefined;
            if (inSubSectionId !== undefined && inSampleSubSectionId !== undefined) {
                dataFieldColor = dataSetColor.fields.find(dfc => {
                    if (dfc.fieldIdentifier === inFieldId && dfc.subSectionIdentifier === inSubSectionId && dfc.sampleSubSectionId === inSampleSubSectionId) {
                        return true;
                    }
                });
            } else if (inSubSectionId !== undefined && inSampleSubSectionId === undefined) {
                dataFieldColor = dataSetColor.fields.find(dfc => {
                    if (dfc.fieldIdentifier === inFieldId && dfc.subSectionIdentifier === inSubSectionId && dfc.sampleSubSectionId === inSampleSubSectionId) {
                        return true;
                    }
                });
            } else {
                dataFieldColor = dataSetColor.fields.find(dfc => dfc.fieldIdentifier === inFieldId);
            }

            if (dataFieldColor !== undefined) {
                dataFieldColor.color = inColor;
            } else {
                dataSetColor.fields.push(
                    new TableItemColor({
                        sectionIdentifier: this.actualSection,
                        fieldIdentifier: inFieldId,
                        color: inColor,
                        subSectionIdentifier: inSubSectionId,
                        sampleSubSectionId: inSampleSubSectionId
                    })
                );
            }
        } else {
            this.tableDataSetColors.push(
                new TableDataSetColor({
                    dataSetid: inDataSetId,
                    fields: [
                        new TableItemColor({
                            sectionIdentifier: this.actualSection,
                            fieldIdentifier: inFieldId,
                            color: inColor,
                            subSectionIdentifier: inSubSectionId,
                            sampleSubSectionId: inSampleSubSectionId
                        })
                    ]
                })
            );
        }
    }

    private setDataSetColors() {
        this.tableDataSetColors.forEach(tdsc => {
            const dataSet = this.dataSets.find(ds => ds.dataSetId === tdsc.dataSetid);
            if (dataSet !== undefined) {
                tdsc.fields.forEach(field => {
                    const editField = this.getEditField(dataSet, field.fieldIdentifier, field.sectionIdentifier, field.subSectionIdentifier, field.sampleSubSectionId);
                    if (editField !== undefined) {
                        editField.color = field.color;
                    }
                });
            }
        });
    }

    /**
     * Method to reset all markers on dataset fields
     * e.g. field color
     */
    private resetDataSetMarkers() {
        this.tableDataSetColors.forEach(tdsc => {
            const dataSet = this.dataSets.find(ds => ds.dataSetId === tdsc.dataSetid);
            if (dataSet !== undefined) {
                tdsc.fields.forEach(field => {
                    const editField = this.getEditField(dataSet, field.fieldIdentifier, field.sectionIdentifier, field.subSectionIdentifier, field.sampleSubSectionId);
                    if (editField !== undefined) {
                        editField.color = TableMenuActionColorEnums.UNMARK;
                    } else {
                        LoggerService.LogWarn(`could not fiond field: `, field, tdsc);
                    }
                });
            }
        });

        this.tableDataSetColors.splice(0, this.tableDataSetColors.length);

        this.setFixedRows(true);
    }

    private quickCopyValue(inValue: string | undefined, inFieldIdentifier: string | undefined) {
        if (inValue !== undefined && inFieldIdentifier !== undefined) {
            const hiddenCopyField: HTMLInputElement = Helpers.getReference<HTMLInputElement>(this.$refs, 'hiddenCopyValue');
            if (hiddenCopyField !== undefined) {
                hiddenCopyField.value = inValue;
                hiddenCopyField.select();
                const copied: boolean = document.execCommand('copy');
                if (copied === true) {
                    LoggerService.LogDebug('Value copied to clipboard: ', hiddenCopyField.value);
                    this.quickCopyToastText = this.$vuetify.lang.t('$vuetify.table.copyToClipboard', this.$vuetify.lang.t(`$vuetify.identifiers.${inFieldIdentifier}`));
                    this.quickCopyToast = true;
                } else {
                    LoggerService.LogWarn('Could not copy value to clipboard');
                }
            }
        }
    }

    private setFixedRows(resetAll: boolean = false) {
        this.fixedRowIdentifiers.forEach(fixed => {
            const dataSet = this.dataSets.find(dsi => dsi.dataSetId === fixed);
            if (dataSet !== undefined) {
                const exist = this.fixedRowItems.find(fri => fri.dataSetId === fixed);
                if (exist === undefined) {
                    dataSet.fixedRow = resetAll ? false : true;
                    this.fixedRowItems.push(new DataSet(dataSet));
                } else {
                    const index = this.fixedRowItems.findIndex(fri => fri.dataSetId === dataSet.dataSetId);
                    if (index >= 0) {
                        dataSet.fixedRow = resetAll ? false : true;
                        this.fixedRowItems[index] = new DataSet(dataSet);
                    }
                }
            }
        });

        if (resetAll === true) {
            this.fixedRowItems.splice(0, this.fixedRowItems.length);
            this.fixedRowIdentifiers.splice(0, this.fixedRowIdentifiers.length);
        } else {
            for (let index = this.fixedRowItems.length - 1; index >= 0; index--) {
                const element = this.fixedRowItems[index];
                if (this.fixedRowIdentifiers.some(id => element.dataSetId === id) === false) {
                    const dataSet = this.dataSets.find(dsi => dsi.dataSetId === element.dataSetId);
                    if (dataSet) {
                        dataSet.fixedRow = false;
                    }
                    this.fixedRowItems.splice(index, 1);
                }
            }
        }

        this.$nextTick(async () => {
            this.setTableHeight();
        });
    }

    private async getDataSets(useFilter?: boolean) {
        // If we are restoring from Vuex "use old dataset" cache, keep it
        if (DataSetStore.GetUseOldDataSet === true) {
            this.dataSets = DataSetService.GetFilteredDataSets;
        } else {
            await DataSetService.FetchFilteredDataSets(useFilter)
                .then(() => {
                    this.dataSets = DataSetService.GetFilteredDataSets;
                })
                .catch(error => {
                    LoggerService.LogError(`Error on fetching filtered data sets: `, error);
                });
        }

        if (this.dataSets.length <= 0) {
            this.showModal = true;
        }

        this.filterResults = this.dataSets.length.toString();
    }

    private async updateDataSets(inDataSetIds?: string[], useFilter: boolean = false) {
        this.isLoading = true;

        DataSetService.UpdateFilteredDataSets(inDataSetIds);

        await this.getDataSets(useFilter);

        try {
            this.listRef!.forceRender();
            this.listRefFixed!.forceRender();
            this.listRefProcess!.forceRender();
        } catch (error) {
            LoggerService.LogError(`scrollerRef[XXX].forceRender: `, error);
            const t = 0;
        }

        this.setDataSetColors();
        this.setFixedRows();
        this.setProcessFilter();

        this.isLoading = false;
    }

    private async setProcessFilter() {
        if (this.processFilterActive(this.processHeaderItems) === true) {
            this.dataSets = DataSetService.GetFilteredDataSets.filter(this.filterByProcess(this.processHeaderItems));
        } else {
            this.dataSets = DataSetService.GetFilteredDataSets;
        }

        if (this.listRef !== undefined) {
            this.listRef.setScrollTop(0);
        }
        if (this.listRefFixed !== undefined) {
            this.listRefFixed.setScrollTop(0);
        }
        if (this.listRefProcess !== undefined) {
            this.listRefProcess.setScrollTop(0);
        }

        try {
            if (this.listRef !== undefined) {
                this.listRef.forceRender();
            }
            if (this.listRefFixed !== undefined) {
                this.listRefFixed.forceRender();
            }
            if (this.listRefProcess !== undefined) {
                this.listRefProcess.forceRender();
            }
        } catch (error) {
            LoggerService.LogWarn(`forceRender on virtuial list causes an error: `, error);
        }
    }

    private filterByUnfixed(inIdentifiers: string[]) {
        return (inDataSet: TableDataSet) => {
            return !inIdentifiers.some(id => inDataSet.dataSetId === id);
        };
    }

    private filterByProcess(inMachineOrder: ConfigurationField[]) {
        return (inDataSet: DataSet) => {
            // if no process filter is slected return
            if (inMachineOrder.some(item => item.valueBool === true) === false) {
                return true;
            }
            // check which processes are selected
            const mo = this.emptyTableDataSet.sections.find(sec => sec.identifier === ApiIdentifers.machineOrder);
            mo!.fields.forEach(x => {
                x.valueBool = inDataSet.sections.some(sec => sec.identifier === x.identifier);
            });

            if (mo !== undefined && mo.fields !== undefined) {
                const dataSetusedMO = mo.fields.filter(field => field.valueBool && field.valueBool === true);
                const usedInMO = inMachineOrder.filter(field => field.valueBool && field.valueBool === true);
                // check if dataSet has all selected processes (AND)
                if (dataSetusedMO.length > 0 && usedInMO.length > 0) {
                    let use = false;
                    for (const item of usedInMO) {
                        const found = dataSetusedMO.find(x => x.identifier === item.identifier);
                        if (found) {
                            if (found.valueBool && found.valueBool === true) {
                                use = true;
                            } else {
                                use = false;
                                break;
                            }
                        } else {
                            use = false;
                            break;
                        }
                    }
                    return use;
                }
            } else {
                return false;
            }
        };
    }

    private processFilterActive(inMachineOrder: ConfigurationField[]): boolean {
        let active = false;
        for (const pf of inMachineOrder) {
            if (pf.valueBool && pf.valueBool === true) {
                active = true;
                break;
            }
        }
        return active;
    }

    /**
     * Sort section fields/subfields to have normal fields at top of subfield arrays
     * @param sectionFields Array of fields
     */
    private sortSectionsFields(sectionFields: ConfigurationField[]) {
        sectionFields.forEach((sectionField: ConfigurationField) => {
            if (sectionField.type === FieldTypeEnum.SUBFIELD && sectionField.fields) {
                this.sortSectionsFields(sectionField.fields);
            }
        });

        sectionFields.sort(this.sortFields);
    }

    private sortFields(lhs: ConfigurationField, rhs: ConfigurationField): number {
        if ((lhs.type === FieldTypeEnum.SUBFIELD || lhs.type === FieldTypeEnum.ACTION_SUBFIELD) && (rhs.type === FieldTypeEnum.SUBFIELD || rhs.type === FieldTypeEnum.ACTION_SUBFIELD)) {
            return 0;
        } else if (lhs.type !== FieldTypeEnum.SUBFIELD && lhs.type !== FieldTypeEnum.ACTION_SUBFIELD && (rhs.type === FieldTypeEnum.SUBFIELD || rhs.type === FieldTypeEnum.ACTION_SUBFIELD)) {
            return -1;
        } else if (rhs.type !== FieldTypeEnum.SUBFIELD && rhs.type !== FieldTypeEnum.ACTION_SUBFIELD && (lhs.type === FieldTypeEnum.SUBFIELD || lhs.type === FieldTypeEnum.ACTION_SUBFIELD)) {
            return 1;
        }
        return 1;
    }

    private async saveState() {
        await DataSetStore.SetUseOldDataSet(true);
        await DataSetStore.SetTableScrollPos(this.listScrollPos);
        await DataSetStore.SetTableDataSetColors(this.tableDataSetColors);
        await DataSetStore.SetFixedLineDataSetIds(this.fixedRowIdentifiers);
        await DataSetStore.SetProcessHeaderItems(this.processHeaderItems);
        await DataSetStore.SetShowProcess(this.showProcess);
        await DataSetStore.SetSelectedSection(this.actualSection);
        await DataSetStore.SetSelectedSubSection(this.selectSubSectionValue);
        await DataSetStore.SetSelectedSampleSection(this.selectSampleSubSectionValue);
    }

    private async setState() {
        // Use copies from store to avoid mutations outside of vuex store
        if (this.tableDataSetColors.length > 0) {
            this.tableDataSetColors = [];
        }
        DataSetStore.GetTableDataSetColors.forEach(x => {
            this.tableDataSetColors.push(new TableDataSetColor(x));
        });
        if (this.fixedRowIdentifiers.length > 0) {
            this.fixedRowIdentifiers = [];
        }
        DataSetStore.GetFixedLineDataSetIds.forEach(x => {
            this.fixedRowIdentifiers.push(x);
        });
        DataSetStore.GetProcessHeaderItems.forEach(x => {
            const item = this.processHeaderItems.find(phi => phi.identifier === x.identifier);
            if (item !== undefined && x.valueBool) {
                item.valueBool = x.valueBool;
            }
        });
        this.showProcess = DataSetStore.GetShowProcess ? true : false;

        this.setDataSetColors();
        this.setFixedRows();
        this.setProcessFilter();

        window.setTimeout(() => {
            if (DataSetStore.GetSelectedSection !== this.actualSection && DataSetStore.GetSelectedSection.length > 0) {
                const headerBar = Helpers.getReference<IHeaderBarComponentMethods>(this.$refs, 'header-bar-table');
                headerBar.setSelectValue(DataSetStore.GetSelectedSection);
            }

            if (DataSetStore.GetSelectedSubSection !== this.selectSubSectionValue && DataSetStore.GetSelectedSubSection.length > 0) {
                this.getHeaderFilterItems(DataSetStore.GetSelectedSubSection);
            }

            if (DataSetStore.GetSelectedSampleSection !== this.selectSampleSubSectionValue && DataSetStore.GetSelectedSampleSection.length > 0) {
                this.selectSampleSubSectionValue = DataSetStore.GetSelectedSampleSection;
            }

            if (this.listRef) {
                this.listRef.setScrollTop(DataSetStore.GetTableScrollPos);
            }
            if (this.listRefFixed) {
                this.listRefFixed.setScrollTop(DataSetStore.GetTableScrollPos);
            }
            if (this.listRefProcess) {
                this.listRefProcess.setScrollTop(DataSetStore.GetTableScrollPos);
            }
        }, 100);

        await DataSetStore.SetUseOldDataSet(false);
    }

    private async clearState() {
        // await DataSetStore.SetUseOldDataSet(false);
        await DataSetStore.ClearTableScrollPos();
        await DataSetStore.ClearTableDataSetColors();
        await DataSetStore.ClearFixedLineDataSetIds();
        await DataSetStore.ClearProcessHeaderItems();
        await DataSetStore.SetShowProcess(false);
    }

    /**
     * Method to calculate the amount of items to show in the virtual list
     */
    private setTableHeight() {
        const windowHeight = window.innerHeight;
        const header = Helpers.getReference<Vue>(this.$refs, 'header-bar-table');
        const tableControls = Helpers.getReference<HTMLDivElement>(this.$refs, 'table-controls');
        const tableHeaders = Helpers.getReference<HTMLDivElement>(this.$refs, 'fixed-table-header');

        const heigth = windowHeight - (header.$el as HTMLDivElement).clientHeight - tableControls.clientHeight - tableHeaders.clientHeight;
        // on item is abour 50px in height, subtract one to have a margin on bottom
        this.virtualListItemsRemain = Math.floor(heigth / 50) - 1 - this.fixedRowItems.length;
        LoggerService.LogDebug(`this.virtualListItemsRemain: ${this.virtualListItemsRemain}`);
    }

    /**
     * Method to change the value of a given table cell/field.
     * If the field is not in the dataSet yet since it was not set,
     * we need to add the field. THis requieres a recursive check
     * if the section and possible fields are alreadey there or needs to be added...
     * @param inDataSet     Selected data set
     * @param inCellId      Identifier of the selected field
     */
    private editCellValue(inDataSet: DataSet, inCellId: string) {
        const field = this.getEditField(inDataSet, inCellId, this.actualSection, this.selectSubSectionValue, this.selectSampleSubSectionValue);
        const tableField = this.getTableField(inCellId, this.actualSection);
        if (tableField !== undefined) {
            const tmp = new TableDataSetField(tableField);
            let tmpUnitType: TableDataSetField | undefined;
            if (tmp.type === FieldTypeEnum.NUMBER && tmp.unit !== undefined) {
                tmpUnitType = this.findUnitTypeField(tmp);
            }
            LoggerService.LogDebug('', this.actualSection, this.selectSubSectionValue, this.selectSampleSubSectionValue);
            if (field !== undefined) {
                // fiueld exists already, just change the value
                tmp.valueBool = field.valueBool;
                tmp.valueNumber = field.valueNumber;
                tmp.valueString = field.valueString;
            } else {
                // field is not set yet, we need to add it...
                let section = inDataSet.sections.find(sec => this.actualSection === sec.identifier);
                const tableSection = this.emptyTableDataSet.sections.find(sec => this.actualSection === sec.identifier);
                if (tableSection !== undefined) {
                    const fieldIdPath = this.getFieldIdPath(tableSection.fields, inCellId);
                    let fieldIdPathUnitType: string[] | undefined;
                    if (tmpUnitType !== undefined) {
                        fieldIdPathUnitType = this.getFieldIdPath(tableSection.fields, tmpUnitType.identifier);
                    }
                    /**
                     * If we are in an action  section like materials, we need to change to
                     * the selected sub-section with correct suffix.
                     * Since this should only be the case on section level, we can simply
                     * replace the first index of fieldIdPath
                     */
                    if (subSectionRegexp.test(this.selectSubSectionValue)) {
                        fieldIdPath[0] = this.selectSubSectionValue;
                        if (fieldIdPathUnitType !== undefined) {
                            fieldIdPathUnitType[0] = this.selectSubSectionValue;
                        }
                    } else if (subSectionRegexp.test(this.selectSampleSubSectionValue)) {
                        fieldIdPath[0] = this.selectSampleSubSectionValue;
                        if (fieldIdPathUnitType !== undefined) {
                            fieldIdPathUnitType[0] = this.selectSampleSubSectionValue;
                        }
                    }

                    // add section if not defined
                    if (section === undefined) {
                        inDataSet.sections.push(
                            new DataSetSection({
                                position: tableSection.position,
                                fields: [],
                                identifier: tableSection.identifier
                            })
                        );
                        section = inDataSet.sections.find(sec => this.actualSection === sec.identifier);
                    }
                    if (section !== undefined) {
                        this.addFieldToFields(section.fields, fieldIdPath);
                        if (fieldIdPathUnitType !== undefined) {
                            this.addFieldToFields(section.fields, fieldIdPathUnitType);
                        }
                    } else {
                        LoggerService.LogError(`editCellValue section not found: ${this.actualSection}`);
                    }
                }
            }

            if (tmpUnitType !== undefined) {
                this.tableEditValueUnitTypeField = tmpUnitType;
                this.tableEditValueUnitTypeField.required = true;
                this.showTableEditValueUnit = true;
            } else {
                this.showTableEditValueUnit = false;
                this.tableEditValueUnitTypeField = new TableDataSetField();
            }

            this.tableEditValueField = tmp;
            this.showTableEditValue = true;
        } else {
            LoggerService.LogWarn(`could not find field(${inCellId}) in dataset: `, inDataSet);
        }
    }

    /**
     * Method to get all field identifiers in correct order to add a field to a dataSet section
     * @param inFields              Array of dataSetFields
     * @param inFieldIdentifier     Field identifier of the field to add
     */
    private getFieldIdPath(inFields: DataSetField[], inFieldIdentifier: string): string[] {
        let pathIds: string[] = [];
        for (const field of inFields) {
            if (field.fields) {
                const subSearch = this.getFieldIdPath(field.fields, inFieldIdentifier);
                if (subSearch.length > 0) {
                    pathIds.push(field.identifier);
                    pathIds = pathIds.concat(subSearch);
                    break;
                }
            } else if (field.identifier === inFieldIdentifier) {
                pathIds.push(inFieldIdentifier);
                break;
            }
        }

        return pathIds;
    }

    /**
     * Method to add a "new" field to a dataSet
     * @param inFields          Array of (section) fields
     * @param inFieldIdPath     Array with field identifiers in correct order
     * @param deep              The actual deep, do not set manully
     */
    private addFieldToFields(inFields: DataSetField[], inFieldIdPath: string[], deep: number = 0) {
        if (deep >= inFieldIdPath.length) {
            LoggerService.LogWarn(`addFieldToSection deep of ${deep} is >= number of array elements ${inFieldIdPath.length}`);
            return;
        }
        let field = inFields.find(f => f.identifier === inFieldIdPath[deep]);
        if (deep === inFieldIdPath.length - 1) {
            if (field === undefined) {
                const pos = inFields.length - 1 >= 0 ? inFields.length - 1 : 0;
                inFields.push(
                    new DataSetField({
                        identifier: inFieldIdPath[deep],
                        position: pos,
                        changed: false
                    })
                );
            } else {
                // this should not happen?
                LoggerService.LogDebug(`field with id: ${inFieldIdPath[deep]} already added`);
            }
        } else {
            if (field === undefined) {
                const pos = inFields.length - 1 >= 0 ? inFields.length - 1 : 0;
                inFields.push(
                    new DataSetField({
                        identifier: inFieldIdPath[deep],
                        position: pos,
                        changed: false,
                        fields: []
                    })
                );
                field = inFields[pos];
            }
            if (field.fields === undefined) {
                field.fields = [];
            }
            this.addFieldToFields(field.fields, inFieldIdPath, ++deep);
        }
    }

    private checkActionSection(inDataSet: DataSet, inSectionIdentifier: string, inSubIdentifier: string, inSubSampleIdentifier?: string): boolean {
        let ret = false;
        const section = inDataSet.sections.find(sec => sec.identifier === inSectionIdentifier);
        if (section !== undefined && section.fields) {
            if (inSubSampleIdentifier !== undefined) {
                const subSample = section.fields.find(subSam => subSam.identifier === inSubSampleIdentifier);
                if (subSample !== undefined && subSample.fields !== undefined) {
                    ret = subSample.fields.some(f => f.identifier === inSubIdentifier);
                }
            } else {
                ret = section.fields.some(f => f.identifier === inSubIdentifier);
            }
        }
        return ret;
    }

    private checkIfIsMachineSection(inIdentifier: string): boolean {
        let isMaschine = false;
        switch (inIdentifier) {
            case ApiIdentifers.blowRoom:
            case ApiIdentifers.carding:
            case ApiIdentifers.integratedDrawFrame:
            case ApiIdentifers.breakerDrawing:
            case ApiIdentifers.lapWinding:
            case ApiIdentifers.combing:
            case ApiIdentifers.finisherDrawing:
            case ApiIdentifers.roving:
            case ApiIdentifers.spinningProcess:
                isMaschine = true;
                break;

            default:
                isMaschine = false;
                break;
        }
        return isMaschine;
    }

    private findUnitTypeField(inField: TableDataSetField): TableDataSetField | undefined {
        const configSections = ConfigurationService.GetCompleteConfigurationSections;
        let found: TableDataSetField | undefined;

        if (inField.unit !== undefined) {
            for (const section of configSections) {
                for (const field of section.fields) {
                    const foundField = this.findFieldByIdentifier(field, inField.unit);
                    if (foundField !== undefined) {
                        found = new TableDataSetField(foundField);
                        break;
                    }
                }
                if (found !== undefined) {
                    break;
                }
            }
        }
        return found;
    }

    /**
     * Find a specific configuration field by identififer
     *
     * @param inField
     * @param inIdentifier
     */
    private findFieldByIdentifier(inField: ConfigurationField, inIdentifier: string): ConfigurationField | undefined {
        let found: ConfigurationField | undefined;

        if (inField.identifier === inIdentifier) {
            found = inField;
        } else if (inField.fields !== undefined) {
            for (const subField of inField.fields) {
                const subFound = this.findFieldByIdentifier(subField, inIdentifier);
                if (subFound !== undefined) {
                    found = subFound;
                    break;
                }
            }
        }

        return found;
    }

    private copyStringToClipboard(inValue: string): void {
        const hiddenCopyField: HTMLInputElement = Helpers.getReference<HTMLInputElement>(this.$refs, 'hiddenCopyValue');
        if (hiddenCopyField !== undefined) {
            hiddenCopyField.value = inValue;
            hiddenCopyField.select();
            const copied: boolean = document.execCommand('copy');
            if (copied === true) {
                LoggerService.LogDebug('Value copied to clipboard: ', hiddenCopyField.value);
                this.quickCopyToastText = this.$vuetify.lang.t('$vuetify.edit.copyToClipboard', inValue);
                this.quickCopyToast = true;
            } else {
                LoggerService.LogWarn('Could not copy value to clipboard');
            }
        }
    }
}
